import {FC, useCallback, useContext, useEffect, useState} from 'react';
import UploadModal from './UploadModal';
import {useParams} from 'react-router-dom';
import axios, {AxiosError} from 'axios';
import MessagebarContext from '../../../../../context/MessagebarContext';
import {DocumentData} from 'resources/assets/js/library/uploadModal';
import WhiteLabelContext from '../../../../../context/WhiteLabelContext';
import {useTranslation} from "react-i18next";

interface UploadModalWrapperProps {
  requestIsCompleted: boolean;
  isOpen: boolean,
  onRequestClose: () => void;
  refetchPageData: () => void;
  documentTypeId: number;
  documentData: DocumentData
}

const UploadModalWrapper: FC<UploadModalWrapperProps> = ({
  requestIsCompleted,
  isOpen,
  onRequestClose,
  documentTypeId,
  documentData,
  refetchPageData
}) => {
  const { t } = useTranslation();
  const [availableFiles, setAvailableFiles] = useState([]);
  const [connectedFiles, setConnectedFiles] = useState([]);
  const [checkedForConnectedFiles, setCheckedForConnectedFiles] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const { requestId } = useParams();
  const { setAlertMessage } = useContext(MessagebarContext);
  const hasEditRights = !requestIsCompleted && documentData?.status !== 'approved';
  const { canUploadMultipleFilesPerDocumentType } = useContext(WhiteLabelContext);

  const getDocumentData = useCallback(async () => {
    setAvailableFiles([]);
    setConnectedFiles([]);
    setCheckedForConnectedFiles(false);

    const { data } = await axios.get(
      `/files/request/document/${documentTypeId}/data`
    );

    if (data) {
      const documents =
        data.documents.length > 0
          ? data.documents.map(({ file_id: fileId, file_name: fileName, download_url: downloadUrl, message: message }) => ({
              fileId,
              fileName,
              downloadUrl,
              isExisting: true,
              message
            }))
          : [];

      setConnectedFiles(documents);
      setCheckedForConnectedFiles(true);
    }
  }, [documentTypeId]);

  const getAvailableFiles = async (query, cb) => {
    const { data } = await axios.get(
      `/files/request/available-files?query=${query || ''}`
    );

    const files = data.map(
      ({ files_id: fileId, original_filename: fileName }) => ({
        fileId,
        fileName,
        canDelete: false
      })
    );

    const unusedFiles = files.filter(
      (f) =>
        !selectedFiles.some((sf) => sf.fileId === f.fileId) &&
        !connectedFiles.some((cf) => cf.fileId === f.fileId)
    );

    setAvailableFiles(unusedFiles);
    if (cb) cb(unusedFiles);
  };

  useEffect(() => {
    if (documentTypeId) getDocumentData();
  }, [getDocumentData, documentTypeId]);

  useEffect(() => {
    if (!isOpen) setSelectedFiles([]);
  }, [isOpen]);

  const handleSubmit = (comment: string): void => {
    const filesIds = selectedFiles.reduce((total, current) => [...total, current.fileId], []);

    // set all selected files to canDelete = false just in case any edge case race conditions causes unintended deletions
    setSelectedFiles(selectedFiles.map(file => ({ ...file, canDelete: false })));

    axios.post('/files/request/connect', {
      document_request_id: Number(requestId),
      files_ids: filesIds,
      document_types_id: documentTypeId,
      comment: comment
    })
      .catch((response: AxiosError<any, any>) => {
        let alertMessage = 'Er is iets mis gegaan met het delen van een of meerdere bestanden';
        if (response.response?.status === 422) {
          alertMessage = response.response.data.message;
        }

        setAlertMessage(t(alertMessage));
      })
      .then(() => {
        refetchPageData();
        onRequestClose();
      })
  };

  const handleMoveToSelected = file => {
    if (canUploadMultipleFilesPerDocumentType) {
      setSelectedFiles(prevConnFiles => [...prevConnFiles, file]);

      setAvailableFiles(prevAvailableFiles =>
        prevAvailableFiles.filter(({ fileId }) => fileId !== file.fileId)
      );
    } else if (connectedFiles.length < 1) {
      const currentSelectedFiles = selectedFiles;

      setSelectedFiles([file]);

      const currentAvailableFiles = availableFiles.filter(({ fileId }) => fileId !== file.fileId);

      setAvailableFiles([
        ...currentAvailableFiles,
        ...currentSelectedFiles
      ]);
    }
  };

  const handleMoveToAvailable = file => {
    setAvailableFiles(prevConnFiles => [file, ...prevConnFiles]);
    setSelectedFiles(prevAvailableFiles =>
      prevAvailableFiles.filter(({ fileId }) => fileId !== file.fileId)
    );
  };

  return (
    isOpen ? (
      <UploadModal
        {...{
          isOpen,
          onRequestClose,
          documentData,
          selectedFiles,
          availableFiles,
          connectedFiles,
          handleMoveToSelected,
          handleMoveToAvailable,
          handleSubmit,
          hasEditRights,
          getAvailableFiles,
          checkedForConnectedFiles
        }}
      />
    ) : null
  );
};

export default UploadModalWrapper;
